import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface BrandingState {
  companyName: string;
  productName: string;
  productShortName: string;
  tagline: string;
  supportEmail: string;
  copyrightHolder: string;
  assistantName: string;
  /** Logo image URL; empty string when no logo is configured. */
  logoUrl: string;
  /** Header-corner icon URL; empty string when no icon is configured. */
  logoIconUrl: string;
  /** Hero theme: 'dark' (default) or 'light'. Empty falls back to 'dark'. */
  landingTheme: string;
  /** Hero description paragraph. May be a JSON locale map or plain text. */
  heroDescription: string;

  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

// Hardcoded defaults match the backend BrandingInfo.Default. Having them here
// means the first paint of the app never shows a flash of "SmartMatrix" — users
// see "Smart Matrix" / "Kobotto" immediately and the Redux slice is overwritten
// with whatever the backend /api/config/get-main_config returns (which may
// include operator overrides).
const initialState: BrandingState = {
  companyName: 'Kobotto',
  productName: 'Smart Matrix',
  productShortName: 'Smart Matrix',
  tagline: "Crafting Tomorrow's Software Solutions",
  supportEmail: 'support@kobotto.com',
  copyrightHolder: 'Kobotto',
  assistantName: 'Kobo',
  logoUrl: '',
  logoIconUrl: '',
  landingTheme: 'dark',
  heroDescription: '',
  isLoaded: false,
  isLoading: false,
  error: null,
};

const brandingSlice = createSlice({
  name: 'branding',
  initialState,
  reducers: {
    startLoadingBranding: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    setBranding: (state, action: PayloadAction<Omit<BrandingState, 'isLoaded' | 'isLoading' | 'error'>>) => {
      state.companyName = action.payload.companyName;
      state.productName = action.payload.productName;
      state.productShortName = action.payload.productShortName;
      state.tagline = action.payload.tagline;
      state.supportEmail = action.payload.supportEmail;
      state.copyrightHolder = action.payload.copyrightHolder;
      state.assistantName = action.payload.assistantName;
      state.logoUrl = action.payload.logoUrl;
      state.logoIconUrl = action.payload.logoIconUrl;
      state.landingTheme = action.payload.landingTheme;
      state.heroDescription = action.payload.heroDescription;
      state.isLoaded = true;
      state.isLoading = false;
      state.error = null;
    },
    failLoadingBranding: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const { startLoadingBranding, setBranding, failLoadingBranding } = brandingSlice.actions;
export default brandingSlice.reducer;

export const selectBranding = (state: { branding: BrandingState }) => state.branding;
export const selectProductName = (state: { branding: BrandingState }) => state.branding.productName;
export const selectCompanyName = (state: { branding: BrandingState }) => state.branding.companyName;
export const selectAssistantName = (state: { branding: BrandingState }) => state.branding.assistantName;
